import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import TowerDriverDialog from "../components/controlTower/TowerDriverDialog";
import type { DriverOpenOptions } from "../components/controlTower/driverFocus";
import TowerLocateDrivers from "../components/controlTower/TowerLocateDrivers";
import TowerGeoAlerts from "../components/controlTower/TowerGeoAlerts";
import TowerUnassigned from "../components/controlTower/TowerUnassigned";
import TowerSummary from "../components/controlTower/TowerSummary";
import TowerMonitor from "../components/controlTower/TowerMonitor";
import TowerAlertsPanel, { towerAlertCount } from "../components/controlTower/TowerMonitorAlerts";
import { openUnassigned } from "../components/controlTower/locateDrivers";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import "./ControlTower.css";
import { argentinaDateKey } from "../components/controlTower/towerShared";
import {
  LightdataSyncStatus,
  useLightdataStatus,
  useTowerClock,
  useTowerDashboard,
} from "../components/controlTower/useTowerData";

function formatInstant(raw: string | null) {
  return raw
    ? new Date(raw).toLocaleString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Sin sincronización registrada";
}
type TowerView = "summary" | "drivers" | "unassigned" | "locate" | "geo";
const VIEWS: [TowerView, string][] = [
  ["summary", "Resumen"],
  ["drivers", "Monitoreo de choferes"],
  ["unassigned", "Envíos sin asignar"],
  ["locate", "Ubicar choferes de zona"],
  ["geo", "Alertas geográficas"],
];
// Breve explicación de cada vista, como en el resto de los módulos.
const VIEW_DESCRIPTIONS: Record<TowerView, string> = {
  summary: "Cómo viene la operación del día: lo colectado, lo entregado y lo que sigue en reparto.",
  drivers: "Seguimiento de cada chofer por zona: avance, Flex en camino y alertas de inactividad.",
  unassigned: "Envíos sin chofer y los choferes que trabajaron cerca, para encontrar quién lo tiene.",
  locate: "Qué choferes hicieron una localidad o zona, o estuvieron cerca de un envío.",
  geo: "Envíos asignados lejos de la zona habitual de su chofer: posibles errores de despacho.",
};

function syncStatusLabel(status: LightdataSyncStatus | null) {
  if (!status || status.status === "never_synced") return "Sin sincronización exitosa";
  if (status.status === "stale") return "Sincronización atrasada";
  if (status.status === "unknown") return "No se pudo consultar la sincronización";
  return "Sincronización OK";
}

function formatTime(raw: string | null | undefined) {
  return raw
    ? new Date(raw).toLocaleTimeString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—";
}

// Próxima sincronización esperada: la última exitosa más el intervalo.
function nextCycle(lastSync: string | null | undefined, intervalMinutes: number | undefined, nowMs: number) {
  if (!lastSync || !intervalMinutes) return null;
  const next = new Date(lastSync).getTime() + intervalMinutes * 60_000;
  return next < nowMs ? "pendiente" : formatTime(new Date(next).toISOString());
}

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

export default function ControlTower() {
  const { token } = useAuth();
  const { date, nowMs: clockNow } = useTowerClock();
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState<TowerView>("drivers");
  const { data, error } = useTowerDashboard(token, date, refresh);
  const syncStatus = useLightdataStatus(token, refresh);
  // Chofer abierto y, si se llegó desde una alerta o un "de días anteriores",
  // con qué filtro y qué aviso abre.
  const [selected, setSelectedState] = useState<{ id: string; options?: DriverOpenOptions } | null>(null);
  const setSelected = (id: string | null, options?: DriverOpenOptions) => setSelectedState(id ? { id, options } : null);
  // La Torre sigue la jornada argentina, con todos los tipos.
  // Una respuesta del día anterior no se dibuja después de medianoche.
  const visibleData = data?.date === date && data.shippingType === "all" ? data : null;

  useEffect(() => {
    setSelected(null);
  }, [date]);
  const selectedDriver = selected ? visibleData?.drivers.find((d) => d.id === selected.id) : undefined;
  const alertsEnabled = date === argentinaDateKey();
  const unassignedCount = useMemo(() => openUnassigned(visibleData?.drivers || []).length, [visibleData]);
  const operationalDateLabel = visibleData?.source?.operationalDateLabel || "Fecha operativa";
  // Los textos del criterio vienen de la fuente del backend: hoy "colectados",
  // mañana la asignación real, sin cambiar esta pantalla.
  const countLabel = visibleData?.source?.countLabel || "Envíos";
  const scopeDescription = visibleData?.source?.scopeDescription || "";
  const switchView = (next: TowerView) => {
    setSelected(null);
    setView(next);
  };
  // Panel de alertas (campanita) y pedido de mostrar el depósito en Monitoreo.
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [depotRequest, setDepotRequest] = useState(0);
  const alertCount = useMemo(
    () =>
      visibleData && alertsEnabled
        ? towerAlertCount(visibleData.drivers, visibleData.depot?.shipments || [], date, clockNow)
        : 0,
    [visibleData, alertsEnabled, date, clockNow],
  );
  const viewCount = (id: TowerView) =>
    id === "geo" ? visibleData?.geoAlerts?.summary.main || 0 : id === "unassigned" ? unassignedCount : 0;
  const upcoming = visibleData?.sync?.enabled
    ? nextCycle(syncStatus?.lastSuccessfulSync, visibleData.sync.intervalMinutes, clockNow)
    : null;

  return (
    <div className="tower-page">
      <div className="tower-header">
        <nav className="tower-view-tabs" aria-label="Vistas de la Torre">
          {VIEWS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`tower-view-tab${view === id ? " active" : ""}`}
              aria-current={view === id ? "page" : undefined}
              onClick={() => switchView(id)}
            >
              {label}
              {viewCount(id) > 0 && <span className="tower-view-tab-count">{viewCount(id)}</span>}
            </button>
          ))}
        </nav>
        <div className="tower-pills">
          {alertsEnabled && (
            <button
              type="button"
              className={`tower-bell${alertCount ? " has-alerts" : ""}`}
              onClick={() => setAlertsOpen(true)}
              aria-label={`Alertas: ${alertCount}`}
              title="Alertas del día"
              disabled={!visibleData}
            >
              <BellIcon />
              {alertCount > 0 && <span className="tower-bell-badge">{alertCount}</span>}
            </button>
          )}
          <span
            className="tower-pill"
            title={`${syncStatusLabel(syncStatus)} · última sincronización ${formatInstant(syncStatus?.lastSuccessfulSync || null)}`}
          >
            <i className={`tower-dot ${syncStatus?.status || "unknown"}`} aria-hidden="true" />
            Actualizado <b>{formatTime(syncStatus?.lastSuccessfulSync)}</b>
            {visibleData?.sync &&
              (visibleData.sync.enabled ? (
                <em className="tower-pill-badge">Cada {visibleData.sync.intervalMinutes} min</em>
              ) : (
                <em className="tower-pill-badge off">Automática apagada</em>
              ))}
          </span>
          {upcoming && (
            <span className="tower-pill">
              Próximo ciclo <b>{upcoming}</b>
            </span>
          )}
          <span className="tower-pill">
            <CalendarIcon />
            {date.split("-").reverse().join("/")}
          </span>
        </div>
      </div>
      <p className="tower-view-description">{VIEW_DESCRIPTIONS[view]}</p>
      {error && (
        <div className="tower-error" role="alert">
          {error}
          {visibleData && " Se conserva la última lectura; los datos pueden haber cambiado."}
          <Button variant="secondary" onClick={() => setRefresh((v) => v + 1)}>
            Reintentar
          </Button>
        </div>
      )}
      {!visibleData && !error && (
        <Card className="tower-empty">
          <span role="status">Cargando los envíos de hoy…</span>
        </Card>
      )}
      {visibleData && (
        <>
          <div id="tower-panel-summary" className="tower-view-panel" aria-label="Resumen" hidden={view !== "summary"}>
            <TowerSummary
              data={visibleData}
              today={date}
              token={token}
              countLabel={countLabel}
              operationalDateLabel={operationalDateLabel}
              onGoTo={switchView}
              nowMs={clockNow}
            />
          </div>
          <div
            id="tower-panel-drivers"
            className="tower-view-panel"
            aria-label="Monitoreo de choferes"
            hidden={view !== "drivers"}
          >
            <TowerMonitor
              data={visibleData}
              date={date}
              nowMs={clockNow}
              alertsEnabled={alertsEnabled}
              countLabel={countLabel}
              scopeDescription={scopeDescription}
              operationalDateLabel={operationalDateLabel}
              onSelectDriver={setSelected}
              depotRequest={depotRequest}
            />
          </div>
          <div
            id="tower-panel-unassigned"
            className="tower-view-panel"
            aria-label="Envíos sin asignar"
            hidden={view !== "unassigned"}
          >
            {view === "unassigned" && (
              <TowerUnassigned
                data={visibleData}
                today={date}
                token={token}
                operationalDateLabel={operationalDateLabel}
                countLabel={countLabel}
              />
            )}
          </div>
          <div
            id="tower-panel-locate"
            className="tower-view-panel"
            aria-label="Ubicar choferes de zona"
            hidden={view !== "locate"}
          >
            {view === "locate" && (
              <TowerLocateDrivers
                data={visibleData}
                today={date}
                token={token}
                operationalDateLabel={operationalDateLabel}
                countLabel={countLabel}
              />
            )}
          </div>
          <div
            id="tower-panel-geo"
            className="tower-view-panel"
            aria-label="Alertas geográficas"
            hidden={view !== "geo"}
          >
            {view === "geo" && (
              <TowerGeoAlerts
                data={visibleData}
                today={date}
                token={token}
                operationalDateLabel={operationalDateLabel}
                countLabel={countLabel}
              />
            )}
          </div>
        </>
      )}
      {alertsOpen && visibleData && (
        <TowerAlertsPanel
          drivers={visibleData.drivers}
          depot={visibleData.depot?.shipments || []}
          today={date}
          nowMs={clockNow}
          onClose={() => setAlertsOpen(false)}
          onOpenDriver={(driverId, options) => {
            setAlertsOpen(false);
            setSelected(driverId, options);
          }}
          onOpenDepot={() => {
            setAlertsOpen(false);
            setView("drivers");
            setDepotRequest((n) => n + 1);
          }}
        />
      )}
      {selectedDriver && (
        <TowerDriverDialog
          key={`${selectedDriver.id}:${selected?.options?.status || ""}:${selected?.options?.zone || ""}:${selected?.options?.universe || ""}:${selected?.options?.focus?.title || ""}`}
          initialStatus={selected?.options?.status}
          initialZone={selected?.options?.zone}
          initialUniverse={selected?.options?.universe}
          initialMapStatus={selected?.options?.mapStatus}
          focus={selected?.options?.focus}
          driver={selectedDriver}
          date={date}
          operationalDateLabel={operationalDateLabel}
          countLabel={countLabel}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
